import { HTTP_METHODS } from '../../constants/HttpConstants';
import { sendRequestToDoSelectCentralService } from '../../utils/request';
import {
  IAllRolesPayload,
  IRole,
  IApiRole,
  ICreateOrUpdateRolePayload,
} from '../../types/roles.types';
import { DefaultNameToEModule } from '../../components/RecruiterDashboard/CreateRoleTile/CreateRoleTile.constants';
import { EModule } from '../../components/RecruiterDashboard/CreateRoleTile/CreateRoleTile.types';
import { PRODUCT } from '../../constants/RecruiterDashboard/roles.constant';

const convertApiRoleToIRole = (apiRole: IApiRole): IRole => {
  const transformedPrivileges = Object.entries(
    apiRole.details.privileges
  ).reduce((acc, [key, value]) => {
    const module = DefaultNameToEModule[key];
    if (module) {
      acc[module] = value;
    }
    return acc;
  }, {} as Record<EModule, any>);
  return {
    users: {
      active: apiRole.users.active,
      inactive: apiRole.users.inactive,
      pending: apiRole.users.pending,
    },
    details: {
      privileges: transformedPrivileges,
      creator: apiRole.details.creator,
      slug: apiRole.details.slug,
      member_type: apiRole.details.member_type,
      name: apiRole.details.name,
    },
    name: apiRole.name,
  };
};

export const fetchRoles = (payload: IAllRolesPayload) => {
  const { username, companySlug } = payload;
  return sendRequestToDoSelectCentralService<IApiRole[]>({
    method: HTTP_METHODS.GET,
    url: `/platform_rbac/v1/recruiters/${companySlug}/?__env=PLT&__user=${username}&product=${PRODUCT}`,
  }).then((response) => response.data.map(convertApiRoleToIRole));
};

export const createorUpdateRole = (
  payload: ICreateOrUpdateRolePayload,
  isEditMode?: boolean
) => {
  return sendRequestToDoSelectCentralService<IApiRole>({
    method: isEditMode ? HTTP_METHODS.PATCH : HTTP_METHODS.POST,
    url: '/platform_rbac/v1/recruiter_role/',
    data: { ...payload, product: PRODUCT },
  }).then((response) => response?.data);
};

export const updateRole = async (
  id: string,
  role: Partial<IRole>
): Promise<IRole> => {
  const response = await fetch(`/api/roles/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(role),
  });
  if (!response.ok) {
    throw new Error('Failed to update role');
  }
  return response.json();
};

export const deleteRole = async (id: string): Promise<void> => {
  return sendRequestToDoSelectCentralService<any>({
    method: HTTP_METHODS.DELETE,
    url: `/platform_rbac/v1/recruiter_role/${id}?product=${PRODUCT}`,
  }).then((response) => response.data);
};

export const deleteMemberInvite = async (
  id: string,
  username: string
): Promise<void> => {
  return sendRequestToDoSelectCentralService<any>({
    method: HTTP_METHODS.DELETE,
    url: `/api/v1/invite/${id}?__env=PLT&__user=${username}&product=${PRODUCT}`,
  }).then((response) => response.data);
};

export const resendMemberInvite = async (
  id: string,
  username: string
): Promise<void> => {
  return sendRequestToDoSelectCentralService<any>({
    method: HTTP_METHODS.POST,
    url: `/rpc/invite.resend/${id}?__env=PLT&__user=${username}&product=${PRODUCT}`,
  }).then((response) => response.data);
};

export const activateRoleMember = async (
  selectedUserName: string,
  username: string
): Promise<void> => {
  return sendRequestToDoSelectCentralService<any>({
    method: HTTP_METHODS.POST,
    url: `/rpc/user.set_activity/${selectedUserName}/active?__env=PLT&__user=${username}&product=${PRODUCT}`,
  }).then((response) => response.data);
};

export const changeUserRole = async (
  payload: {
    current_role: string;
    new_role: string;
    email: string;
  },
  username: string
) => {
  return sendRequestToDoSelectCentralService({
    method: HTTP_METHODS.POST,
    url: `/rpc/modify_role?__env=PLT&__user=${username}&product=${PRODUCT}`,
    data: payload,
  });
};

export const rolesApi = {
  getAllRoles: (payload: IAllRolesPayload) => fetchRoles(payload),
  createorUpdateRole: (payload: ICreateOrUpdateRolePayload) =>
    createorUpdateRole(payload),
  updateRole: (id: string, role: Partial<IRole>) => updateRole(id, role),
  deleteRole: (id: string) => deleteRole(id),
  changeUserRole: (
    payload: {
      current_role: string;
      new_role: string;
      email: string;
    },
    username: string
  ) => changeUserRole(payload, username),
  resendMemberInvite: (id: string, username: string) =>
    resendMemberInvite(id, username),
  deleteMemberInvite: (id: string, username: string) =>
    resendMemberInvite(id, username),
};
